import { useAuth } from "@clerk/nextjs";
import { GetToken } from "@clerk/types";
import { datadogRum } from "@datadog/browser-rum";
import Intercom, { shutdown, update, onShow } from "@intercom/messenger-js-sdk";
import { useEffect, useState } from "react";
import { useUser } from "@/auth/useUser";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import { useIntercomAdditionalAttributes } from "@/hooks/intercom/useIntercomAdditionalAttributes";
import { CLERK_JWT_INTERCOM_TEMPLATE } from "@/types";
import { DATADOG_ENABLED } from "@/utils/observability/const";
import useErrorLogger from "@/utils/useErrorLogger";
import useWarningLogger from "@/utils/useWarningLogger";

const defaultIntercomConfig = {
  app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
  hide_default_launcher: true,
  custom_launcher_selector: "#intercom-launcher",
};

// This is a Clerk custom template for Intercom that is used to get the user token.
const getIntercomToken = async (getToken: GetToken) => {
  const token = await getToken(CLERK_JWT_INTERCOM_TEMPLATE);

  if (!token) return null;

  return token;
};

export default function IntercomMessenger() {
  const { isLoaded: isClerkLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { intercomV1Enabled } = useFeatureFlags();
  const [isIntercomInitialized, setIsIntercomInitialized] = useState(false);
  const [intercomToken, setIntercomToken] = useState<string | null>(null);
  const logError = useErrorLogger();
  const logWarning = useWarningLogger();

  const intercomAdditionalAttributes = useIntercomAdditionalAttributes();

  const isWindowDefined = typeof window !== "undefined";

  const isReadyToInit =
    isWindowDefined && !!user?.id && isSignedIn && intercomV1Enabled;

  // Right now when switching medspas (e.g GFE reviewers) or roles that have multiple medspas
  // won't update the medspa field in Intercom.
  useEffect(() => {
    if (!isClerkLoaded) return;

    const fetchIntercomToken = async () => {
      try {
        const token = await getIntercomToken(getToken);
        setIntercomToken(token);
      } catch (e) {
        logError("Error fetching Intercom token", e);
      }
    };

    fetchIntercomToken();
  }, [isClerkLoaded, getToken, logError]);

  // Initialize Intercom
  useEffect(() => {
    if (isReadyToInit && intercomToken) {
      try {
        Intercom({
          ...defaultIntercomConfig,
          intercom_user_jwt: intercomToken,
        });
        setIsIntercomInitialized(true);
      } catch (error) {
        logError("Error initializing Intercom", error);
      }
    }
  }, [isReadyToInit, intercomToken, logError]);

  // Update Intercom with session replay link when user opens messenger
  useEffect(() => {
    if (isIntercomInitialized) {
      onShow(() => {
        if (!DATADOG_ENABLED || typeof window === "undefined") return;

        try {
          const link = datadogRum.getSessionReplayLink?.() ?? null;
          if (link) {
            update({ session_replay_link: link });
          }
        } catch (error) {
          logWarning("Error updating Intercom with session replay link", error);
        }
      });
    }
  }, [isIntercomInitialized, logWarning]);

  // Update Intercom with custom attributes when ready
  useEffect(() => {
    if (
      isIntercomInitialized &&
      intercomAdditionalAttributes &&
      Object.keys(intercomAdditionalAttributes).length > 0
    ) {
      try {
        update(intercomAdditionalAttributes);
      } catch (error) {
        logWarning("Error updating Intercom with additional attributes", error);
      }
    }
  }, [isIntercomInitialized, intercomAdditionalAttributes, logWarning]);

  // Shutdown Intercom when user is signed out
  useEffect(() => {
    if (user === undefined && !isSignedIn) {
      shutdown();
      setIntercomToken(null);
      setIsIntercomInitialized(false);
    }
  }, [user, isSignedIn]);

  return null;
}
